import { Injectable } from '@angular/core';
import { IFieldModel } from '../../models/blueprint/blueprint-field.model';
import { IFieldValueConfigurationModel } from '../../models/app-setting/app-setting.model';
import { BlueprintDataHelper } from './blueprint-data.helper';

@Injectable({ providedIn: 'root' })
export class BlueprintPropertyBindingHelper {
  constructor(private blueprintDataHelper: BlueprintDataHelper) {}

  /**
   * Parse colorData string from propertyBinding
   * Format: "To Do:#6c7481\nIn Progress:#c1cfe9\nDone:#6dbd4a\n"
   * Returns Map<statusName (lowercase), color>
   */
  public parseColorData(colorDataString: string): Map<string, string> {
    const colorMap = new Map<string, string>();

    if (!colorDataString) {
      return colorMap;
    }

    const colorValueDataList = colorDataString.split('\n');
    // Remove last element because it is empty
    colorValueDataList.splice(-1, 1);

    colorValueDataList.forEach((row) => {
      const [status, color] = row.split(':');
      if (status && color) {
        colorMap.set(status.toLowerCase().trim(), color.trim());
      }
    });

    return colorMap;
  }

  /**
   * Build IFieldValueConfigurationModel[] from blueprint field options + propertyBinding colors
   * @param statusField The status field from blueprint.fields
   * @param propertyBinding The propertyBinding array from blueprintNode
   * @param statusFieldId The ID of the status field to find in propertyBinding
   * @param nodeTypeName The node type name (e.g., "epic", "story")
   * @returns Array of field value configurations for status icons
   */
  public buildStatusIconsFromBlueprint(statusField: IFieldModel | null, propertyBinding: any[] | null, statusFieldId: string, nodeTypeName: string): IFieldValueConfigurationModel[] {
    if (!statusField?.value) {
      return [];
    }

    const value = statusField.value;

    // Status dropdown format: StatusOptions array with per-option color data (Pascal or camel case from API)
    const statusOptions: any[] = value.StatusOptions ?? value.statusOptions;
    if (Array.isArray(statusOptions) && statusOptions.length > 0) {
      // Sort to the canonical board/graph order (category → order → name) so the edit-panel
      // status picker matches the board columns, the status graph, and the table dropdowns.
      return this.blueprintDataHelper.sortStatusOptionsForDisplay(statusOptions).map((opt, index) => {
        const text: string = opt.StatusText ?? opt.statusText ?? '';
        const fillColor: string = opt.FillColor ?? opt.fillColor ?? '#d3d3d3';
        const textColor: string | undefined = opt.TextColor ?? opt.textColor ?? '#000000';
        const borderColor: string | undefined = opt.BorderColor ?? opt.borderColor;
        const borderThickness: number | undefined = opt.BorderThickness ?? opt.borderThickness;
        return {
          key: text.toLowerCase(),
          tooltip: text,
          alt: text,
          style: { color: fillColor, textColor, borderColor, borderThickness },
          icon: '',
          nodeType: [nodeTypeName.toLowerCase()],
          isStepStatus: false,
          rank: index,
        };
      });
    }

    // Standard dropdown format: plain Options array without statusOptions — return with default colors
    const options: string[] = value.Options ?? value.options;
    if (!Array.isArray(options) || options.length === 0) {
      return [];
    }

    return options.map((option, index) => ({
      key: option.toLowerCase(),
      tooltip: option,
      alt: option,
      style: { color: '#d3d3d3', textColor: '#000000' },
      icon: '',
      nodeType: [nodeTypeName.toLowerCase()],
      isStepStatus: false,
      rank: index,
    }));
  }
}
